import React, { lazy } from "react";
import {
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
  useParams,
} from "react-router-dom";
import Layout from "@/components/Layout";
import useAuthStore from "@/store/useAuthStore";
import { useChapterViewerRole } from "@/hooks/domain/useChapterViewerRole";
import { homePathForUser } from "@/utils/homePath";
import { isPublicAppPath } from "@/utils/publicRoutes";

// Retrocompatibilità: diversi punti dell'app importano `homePathForUser` da
// "@/router" (OAuthSuccess, SetupWizardPage, test). L'implementazione vive in
// utils/homePath.ts per evitare import circolari con EventsEntry.
export { homePathForUser };

export function LoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-950">
      <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

export function ProtectedRoute() {
  const user = useAuthStore((s) => s.user);
  const isHydrated = useAuthStore((s) => s.isHydrated);
  const location = useLocation();
  if (!isHydrated) return <LoadingFallback />;
  // PAGINE PUBBLICHE DELL'APP (Ott 2026): /events deve essere leggibile anche
  // senza sessione, altrimenti Google vede solo un redirect a /login
  // (Search Console: «Pagina con reindirizzamento»).
  if (!user) {
    if (isPublicAppPath(location.pathname)) return <Outlet />;
    return <Navigate to="/login" replace />;
  }
  return <Outlet />;
}

/** FIX V-02: BikerRoute — Impedisce a dealer/admin di accedere alla dashboard biker.
 *  Le API sono già protette da denyRole('dealer') lato server,
 *  ma l'UI veniva comunque renderizzata, causando confusione. */
export function BikerRoute() {
  const user = useAuthStore((s) => s.user);
  const isHydrated = useAuthStore((s) => s.isHydrated);
  if (!isHydrated) return <LoadingFallback />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.user_type === "dealer")
    return <Navigate to="/dealer/dashboard" replace />;
  if (user.user_type === "admin") return <Navigate to="/admin" replace />;
  return <Outlet />;
}

export function AdminRoute() {
  const user = useAuthStore((s) => s.user);
  const isHydrated = useAuthStore((s) => s.isHydrated);
  if (!isHydrated) return <LoadingFallback />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.user_type !== "admin") return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

export function DealerRoute() {
  const user = useAuthStore((s) => s.user);
  const isHydrated = useAuthStore((s) => s.isHydrated);
  const localePending = useAuthStore((s) => s.localePending);
  if (!isHydrated) return <LoadingFallback />;
  if (!user) return <Navigate to="/login" replace />;
  // LOCALE/TAPPA (Ott 2026): il locale ha user_type "dealer" ma gestisce una
  // tappa, non un concessionario: niente menu/avvisi di abbonamento e chapter.
  // Lo mandiamo alla sua area, anche per i link diretti /dealer/*.
  //
  // ATTENZIONE: aspettiamo SOLO mentre la verifica è in corso (`localePending`,
  // con timeout lato LocaleResolver). Prima si aspettava `is_locale ===
  // undefined` e l'app restava bloccata sulla rotellina (bug 19 ott 2026).
  if (
    user.user_type === "dealer" &&
    user.is_locale === undefined &&
    localePending
  ) {
    return <LoadingFallback />;
  }
  if (user.is_locale) return <Navigate to="/locale" replace />;
  if (user.user_type !== "dealer" && user.user_type !== "admin")
    return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

export function ChapterOwnerRoute() {
  const user = useAuthStore((s) => s.user);
  const { id: chapterId } = useParams<{ id: string }>();
  const viewerRoleState = useChapterViewerRole(chapterId);

  if (!user) return <Navigate to="/login" replace />;
  if (viewerRoleState.status === "loading") return <LoadingFallback />;

  if (
    viewerRoleState.status === "ok" &&
    (viewerRoleState.role === "chapter_owner" ||
      viewerRoleState.role === "primary_officer")
  ) {
    return <Outlet />;
  }

  return <Navigate to={`/chapters/${chapterId ?? ""}`} replace />;
}

export function RootRedirect() {
  const user = useAuthStore((s) => s.user);
  const isHydrated = useAuthStore((s) => s.isHydrated);
  const localePending = useAuthStore((s) => s.localePending);
  // Se l'auth non è ancora caricato, mostra fallback (evita flash landing → login)
  if (!isHydrated) return <LoadingFallback />;
  // Se utente loggato → onboarding first-ride (se nuovo) o dashboard
  if (user) {
    // LOCALE/TAPPA (Ott 2026): attendiamo la verifica solo se è in corso
    // (timeout garantito in LocaleResolver) — mai un'attesa infinita.
    if (
      user.user_type === "dealer" &&
      user.is_locale === undefined &&
      localePending
    ) {
      return <LoadingFallback />;
    }
    // FIX BUG #30 (Agosto 2026): Due flussi onboarding sovrapposti.
    // SetupWizardPage (/setup) è il wizard nuovo (Step 1/4).
    // FirstRideOnboarding (/first-ride) era il wizard legacy (1 di 3).
    // Reindirizziamo a /setup per consolidare.
    //
    // BUG #44 FIX: Il flag rc-setup-done può perdersi (localStorage cleared,
    // incognito, nuovo dispositivo). Controllo aggiuntivo: se l'utente ha già
    // almeno 1 moto nel garage, salta il wizard.
    const setupDone =
      localStorage.getItem("rc-setup-done") ||
      sessionStorage.getItem("rc-setup-done") ||
      (user as any)?.garage_bikes_count > 0 ||
      (user as any)?.bikes_count > 0;
    if (
      !setupDone &&
      user.user_type !== "admin" &&
      user.user_type !== "dealer"
    ) {
      return <Navigate to="/setup" replace />;
    }
    return (
      <Navigate to={homePathForUser(user.user_type, user.is_locale)} replace />
    );
  }
  // Utente non autenticato → landing page pubblica
  return <LandingPage />;
}

const OnboardingPage = lazy(() => import("./pages/OnboardingPage"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
// TAPPE (Ott 2026): pagina pubblica di candidatura dei locali + adesivo stampabile.
const TappeLanding = lazy(() => import("./pages/tappe/TappeLanding"));
const TappeList = lazy(() => import("./pages/tappe/TappeList"));
const TappaDetail = lazy(() => import("./pages/tappe/TappaDetail"));
const TappeSuggest = lazy(() => import("./pages/tappe/TappeSuggest"));
const TappePromo = lazy(() => import("./pages/tappe/TappePromo"));
const TappaCheckin = lazy(() => import("./pages/tappe/TappaCheckin"));
const TappeEvents = lazy(() => import("./pages/tappe/TappeEvents"));
const TappeReviews = lazy(() => import("./pages/tappe/TappeReviews"));
const TappePoster = lazy(() => import("./pages/tappe/TappePoster"));
const LocalePanel = lazy(() => import("./pages/locale/LocalePanel"));
const LocaleOverview = lazy(
  () => import("./pages/locale/sections/LocaleOverview"),
);
const LocalePromos = lazy(() => import("./pages/locale/sections/LocalePromos"));
const LocaleMessages = lazy(
  () => import("./pages/locale/sections/LocaleMessages"),
);
const LocaleEvents = lazy(() => import("./pages/locale/sections/LocaleEvents"));
// OTT 2026: guadagni delle foto vendute (Photo Shop del locale)
const LocaleEarnings = lazy(
  () => import("./pages/locale/sections/LocaleEarnings"),
);
const LocaleCustomers = lazy(
  () => import("./pages/locale/sections/LocaleCustomers"),
);
const LocaleData = lazy(() => import("./pages/locale/sections/LocaleData"));
// OTT 2026: locandine e adesivi multi-formato con QR del codice tappa
const LocalePrint = lazy(() => import("./pages/locale/sections/LocalePrint"));
const LocaleSecurity = lazy(
  () => import("./pages/locale/sections/LocaleSecurity"),
);
const LocaleBilling = lazy(
  () => import("./pages/locale/sections/LocaleBilling"),
);
const LocaleReviews = lazy(
  () => import("./pages/locale/sections/LocaleReviews"),
);
const LocaleTeam = lazy(() => import("./pages/locale/sections/LocaleTeam"));
const TappeBadge = lazy(() => import("./pages/tappe/TappeBadge"));
const SetupWizardPage = lazy(() => import("./pages/SetupWizardPage"));
const FirstRideOnboarding = lazy(() => import("./pages/FirstRideOnboarding"));

const Login = lazy(() => import("./pages/auth/Login"));
const Register = lazy(() => import("./pages/auth/Register"));
const RegisterDealer = lazy(() => import("./pages/auth/RegisterDealer"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Cookies = lazy(() => import("./pages/Cookies"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
// CENTRO ASSISTENZA (Ott 2026): FAQ ricercabili prima del modulo di contatto.
const HelpCenterPage = lazy(() => import("./pages/HelpCenterPage"));
const ActivateChapter = lazy(() => import("./pages/auth/ActivateChapter"));
const InviteEvent = lazy(() => import("./pages/events/InviteEvent"));
const RideGuardLive = lazy(() => import("./pages/rides/RideGuardLive"));
const DealerPublicShowcase = lazy(
  () => import("./pages/dealers/DealerPublicShowcase"),
);
const DealerPublicRentalFleet = lazy(
  () => import("./pages/dealers/DealerPublicRentalFleet"),
);
const OAuthSuccess = lazy(() => import("./pages/auth/OAuthSuccess"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const PlanRoute = lazy(() => import("./pages/routes/PlanRoute"));
const RecordRoute = lazy(() => import("./pages/routes/RecordRoute"));
const RouteDetail = lazy(() => import("./pages/routes/RouteDetail"));
const RoutesList = lazy(() => import("./pages/routes/RoutesList"));
const EditRoute = lazy(() => import("./pages/routes/EditRoute"));
const GenerateByDistance = lazy(
  () => import("./pages/routes/GenerateByDistance"),
);
const Garage = lazy(() => import("./pages/garage/Garage"));
const BikeDetail = lazy(() => import("./pages/garage/BikeDetail"));
const ChapterCreate = lazy(() => import("./pages/chapters/ChapterCreate"));
const ChapterDetail = lazy(() => import("./pages/chapters/ChapterDetail"));
const ChaptersList = lazy(() => import("./pages/chapters/ChaptersList"));
const UserChapters = lazy(() => import("./pages/users/UserChapters"));
const CreateEvent = lazy(() => import("./pages/events/CreateEvent"));
const EditEvent = lazy(() => import("./pages/events/EditEvent"));
const EventDetail = lazy(() => import("./pages/events/EventDetail"));
// COLLEGAMENTI (28 Ott 2026): due pagine complete erano irraggiungibili — gli
// endpoint esistevano gia' (`/api/events/:id/briefing`, `/api/users/me/passport`).
const RideBriefingPage = lazy(() => import("./pages/events/RideBriefingPage"));
const EventPassportPage = lazy(
  () => import("./pages/events/EventPassportPage"),
);
const EventsList = lazy(() => import("./pages/events/EventsList"));
// PAGINA EVENTI (Ott 2026): /events è pubblica e indicizzabile. Per chi è
// loggato resta l'hub di sempre; per anonimi/crawler mostra PublicEventsPage.
const EventsEntry = lazy(() => import("./pages/events/EventsEntry"));
const Nearby = lazy(() => import("./pages/social/Nearby"));
const Board = lazy(() => import("./pages/social/Board"));
const Notifications = lazy(() => import("./pages/social/Notifications"));
const DealerOffers = lazy(() => import("./pages/DealerOffers"));
const TeamInvites = lazy(() => import("./pages/TeamInvites"));
const Chats = lazy(() => import("./pages/social/Chats"));
const Radar = lazy(() => import("./pages/social/Radar"));
const Subscribe = lazy(() => import("./pages/subscription/Subscribe"));
const CheckoutPage = lazy(() => import("./pages/subscription/CheckoutPage"));
const ManageSubscription = lazy(
  () => import("./pages/subscription/ManageSubscription"),
);
const Profile = lazy(() => import("./pages/users/Profile"));
const UserSettings = lazy(() => import("./pages/users/UserSettingsPage"));
const MyAppointments = lazy(() => import("./pages/users/MyAppointments"));
const GarageAccessPage = lazy(() => import("./pages/users/GarageAccessPage"));
const AdminPaymentSettingsPage = lazy(
  () => import("./pages/Admin/AdminPaymentSettingsPage"),
);
const DealersList = lazy(() => import("./pages/dealers/DealersList"));
const DealerDetail = lazy(() => import("./pages/dealers/DealerDetail"));
const DealersNearby = lazy(() => import("./pages/dealers/DealersNearby"));
// FIX RIDES-ORPHAN (Ott 2026): RidesList/RideCreate/RideDetail erano lazily
// importati ma NESSUNA rotta li renderizzava (/rides, /rides/new, /rides/:id
// reindirizzano a /routes/record) e chiamavano `/api/rides`, endpoint
// inesistente: file rimossi insieme agli import.
const ChapterDashboard = lazy(
  () => import("./pages/chapters/ChapterDashboardV2"),
);
const ChapterStats = lazy(() => import("./pages/chapters/ChapterStats"));
const ChapterDocuments = lazy(
  () => import("./pages/chapters/ChapterDocuments"),
);
const ChapterBroadcast = lazy(
  () => import("./pages/chapters/ChapterBroadcast"),
);
const ChapterRoles = lazy(() => import("./pages/chapters/ChapterRoles"));
const ChapterBoard = lazy(() => import("./pages/chapters/ChapterBoard"));
const ChapterChallenges = lazy(
  () => import("./pages/chapters/ChapterChallenges"),
);
const ChapterQuotas = lazy(() => import("./pages/chapters/ChapterQuotas"));
// ROMBO-P2 (Ondata 2): tesseramento + premi chilometri del chapter
const ChapterMembership = lazy(
  () => import("./pages/chapters/ChapterMembership"),
);
const EventCopilot = lazy(() => import("./pages/events/EventCopilot"));
const EventsMapPage = lazy(() => import("./pages/events/EventsMapPage"));
const EventClose = lazy(() => import("./pages/events/EventClose"));
const EventPayments = lazy(() => import("./pages/events/EventPayments"));
const EventBudget = lazy(() => import("./pages/events/EventBudget"));
const LeaderboardPage = lazy(() => import("./pages/social/LeaderboardPage"));
const MyStats = lazy(() => import("./pages/users/MyStats"));
const DealerRegister = lazy(() => import("./pages/dealers/DealerRegister"));
const DealerUpgrade = lazy(() => import("./pages/dealers/DealerUpgrade"));
const DealerDashboard = lazy(() => import("./pages/dealers/DealerDashboardV2"));
const DealerShowcase = lazy(() => import("./pages/dealers/DealerShowcase"));
const DealerRentalFleet = lazy(
  () => import("./pages/dealers/DealerRentalFleet"),
);
const DealerChapterDetail = lazy(
  () => import("./pages/dealers/DealerChapterDetail"),
);
const DealerChapters = lazy(() => import("./pages/dealers/DealerChapters"));
const DealerAffiliations = lazy(
  () => import("./pages/dealers/DealerAffiliations"),
);
const NearbyDealer = lazy(() => import("./pages/dealers/NearbyDealer"));
const DealerAnalytics = lazy(() => import("./pages/dealer/DealerAnalytics"));
const DealerEvents = lazy(() => import("./pages/dealer/DealerEvents"));
const DealerAppointments = lazy(
  () => import("./pages/dealer/DealerAppointments"),
);
const DealerPromotions = lazy(() => import("./pages/dealer/DealerPromotions"));
const DealerSubscription = lazy(
  () => import("./pages/dealer/DealerSubscription"),
);
const DealerFollowups = lazy(() => import("./pages/dealer/DealerFollowups"));
const DealerCommunications = lazy(
  () => import("./pages/dealer/DealerCommunications"),
);
const DealerProfile = lazy(() => import("./pages/dealer/DealerProfile"));
const DigestSettings = lazy(() => import("./pages/settings/DigestSettings"));
const DealerLeadsKanban = lazy(
  () => import("./pages/dealer/DealerLeadsKanban"),
);
const Admin = lazy(() => import("./pages/Admin"));
const AdminOpsDashboardPage = lazy(
  () => import("./pages/AdminOpsDashboardPage"),
);
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const AdminUserDetail = lazy(() => import("./pages/Admin/AdminUserDetail"));
const AdminModerationPage = lazy(
  () => import("./pages/Admin/AdminModerationPage"),
);
const AdminSettingsPage = lazy(() => import("./pages/Admin/AdminSettingsPage"));
const AdminFeatureFlagsPage = lazy(
  () => import("./pages/Admin/AdminFeatureFlagsPage"),
);
const AdminSecurityPage = lazy(() => import("./pages/Admin/AdminSecurityPage"));
// FIX AB-ADMIN (Ott 2026): pagina A/B finalmente raggiungibile (prima esisteva
// solo il file, senza rotta).
const AbTestingAdminPage = lazy(
  () => import("./pages/Admin/AbTestingAdminPage"),
);
const AdminDashboardPage = lazy(
  () => import("./pages/Admin/AdminDashboardPage"),
);
const AdminToolsPage = lazy(() => import("./pages/Admin/AdminToolsPage"));
const AdminEnterprisePage = lazy(
  () => import("./pages/Admin/AdminEnterprisePage"),
);
// FEAT ADMIN-MONITORING (18 Set 2026): vista Monitoraggio Utenti
const AdminUserMonitoringPage = lazy(
  () => import("./pages/Admin/AdminUserMonitoringPage"),
);
const BroadcastCenterPage = lazy(
  () => import("./pages/Admin/BroadcastCenterPage"),
);
// FEAT OUTREACH (22 Ott 2026): sezione «Presentazione» (Biker / Tappe)
const AdminOutreachPage = lazy(() => import("./pages/Admin/AdminOutreachPage"));
const AdminFinancialsPage = lazy(
  () => import("./pages/Admin/AdminFinancialsPage"),
);
const AdminEventsPage = lazy(() => import("./pages/Admin/AdminEventsPage"));
// TAPPE (Ott 2026): candidature dei locali con dati fiscali
const AdminTappePage = lazy(() => import("./pages/Admin/AdminTappePage"));
const AdminNewsPage = lazy(() => import("./pages/Admin/AdminNewsPage"));
const AdminAnalytics = lazy(() => import("./pages/Admin/AdminAnalytics"));
const AdminAgentDetailPage = lazy(
  () => import("./pages/Admin/AdminAgentDetailPage"),
);
// Pannello Agente (FIX AFFILIATE Set 2026)
const AgentDashboard = lazy(() => import("./pages/agent/AgentDashboard"));
// Partner / Agente Area
const PartnerLayout = lazy(() => import("./components/partner/PartnerLayout"));
const PartnerDashboard = lazy(() => import("./pages/partner/PartnerDashboard"));
const PartnerReferralCodes = lazy(
  () => import("./pages/partner/PartnerReferralCodes"),
);
const PartnerCustomers = lazy(() => import("./pages/partner/PartnerCustomers"));
const PartnerCommissions = lazy(
  () => import("./pages/partner/PartnerCommissions"),
);
const PartnerProfile = lazy(() => import("./pages/partner/PartnerProfile"));
// V2 Monetization pages
const UnifiedPricing = lazy(
  () => import("./pages/subscription/UnifiedPricing"),
);
const ReferralPage = lazy(() => import("./pages/referral/ReferralPage"));
// V4 — Roadprint, Badges, Chapter Wars
const RoadprintPage = lazy(() => import("./pages/users/RoadprintPage"));
const BadgesPage = lazy(() => import("./pages/users/BadgesPage"));
const ChapterWarsPage = lazy(() => import("./pages/social/ChapterWarsPage"));
// V7 — Social Feed, My Rides, Calendar
const SocialFeedPage = lazy(() => import("./pages/social/SocialFeedPage"));
const MyRidesPage = lazy(() => import("./pages/users/MyRidesPage"));
const RidePassportPage = lazy(() => import("./pages/users/RidePassportPage"));
const RideWrappedPage = lazy(() => import("./pages/users/RideWrappedPage"));
const EventCalendar = lazy(() => import("./pages/events/EventCalendar"));
// Challenges
const ChallengesPage = lazy(() => import("./pages/challenges/ChallengesPage"));
const ChallengeDetailPage = lazy(
  () => import("./pages/challenges/ChallengeDetailPage"),
);
const ChapterPricing = lazy(() => import("./pages/chapters/ChapterPricing"));
// V8 — Cerchia Bikers Preferiti
const CerchiaPage = lazy(() => import("./pages/social/CerchiaPage"));
// V8 — Garage Marketplace (P2P vendite Premium)
const Marketplace = lazy(() => import("./pages/garage/Marketplace"));
const MarketplaceDetail = lazy(
  () => import("./pages/garage/MarketplaceDetail"),
);

export default function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/register/collaborator" element={<Register />} />
      <Route path="/register/dealer" element={<RegisterDealer />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/tappe" element={<TappeLanding />} />
      <Route path="/tappe/elenco" element={<TappeList />} />
      {/* Scheda pubblica del locale (Ott 2026): logo, posizione, pubblicita, eventi. */}
      <Route path="/tappe/:id" element={<TappaDetail />} />
      <Route path="/tappe/segnala" element={<TappeSuggest />} />
      <Route path="/tappe/promo" element={<TappePromo />} />
      {/* QR del bancone: check-in della presenza (Ott 2026) */}
      <Route path="/tappa/checkin" element={<TappaCheckin />} />
      <Route path="/tappe/eventi" element={<TappeEvents />} />
      {/* Recensioni della tappa (Ott 2026): /tappe/recensioni?code=TAPPA-XXXX */}
      <Route path="/tappe/recensioni" element={<TappeReviews />} />
      <Route path="/tappe/locandina" element={<TappePoster />} />
      {/* AREA LOCALE (Ott 2026): struttura dedicata ai locali, con sezioni.
          Non passa dall'area concessionario: nessun menu/avviso dealer. */}
      <Route path="/locale" element={<LocalePanel />}>
        <Route index element={<LocaleOverview />} />
        <Route path="pubblicita" element={<LocalePromos />} />
        <Route path="comunicazioni" element={<LocaleMessages />} />
        <Route path="eventi" element={<LocaleEvents />} />
        <Route path="guadagni" element={<LocaleEarnings />} />
        <Route path="clienti" element={<LocaleCustomers />} />
        <Route path="recensioni" element={<LocaleReviews />} />
        <Route path="squadra" element={<LocaleTeam />} />
        <Route path="dati" element={<LocaleData />} />
        <Route path="locandina" element={<LocalePrint />} />
        <Route path="fatturazione" element={<LocaleBilling />} />
        <Route path="sicurezza" element={<LocaleSecurity />} />
      </Route>
      <Route path="/tappe/badge" element={<TappeBadge />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/cookies" element={<Cookies />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/aiuto" element={<HelpCenterPage />} />
      <Route path="/chapters/activate" element={<ActivateChapter />} />
      <Route path="/invite/event/:token" element={<InviteEvent />} />
      <Route path="/live/:token" element={<RideGuardLive />} />
      <Route path="/dealers/:id/showcase" element={<DealerPublicShowcase />} />
      <Route path="/dealers/:id/rental" element={<DealerPublicRentalFleet />} />
      <Route path="/auth/oauth-success" element={<OAuthSuccess />} />
      <Route path="/pricing" element={<UnifiedPricing />} />
      {/* MONETIZATION-P4: pricing pubblico chapter (vantaggio competitivo) */}
      <Route path="/pricing/chapters" element={<ChapterPricing />} />
      <Route path="/onboarding" element={<OnboardingPage />} />

      <Route element={<ProtectedRoute />}>
        <Route path="/dealer/register" element={<DealerRegister />} />
        <Route path="/dealer/upgrade" element={<DealerUpgrade />} />
        <Route path="/setup" element={<SetupWizardPage />} />
        <Route path="/first-ride" element={<FirstRideOnboarding />} />

        <Route element={<Layout />}>
          {/* FIX ADMIN-EVENTS: /events/:id e /events/:id/payments fuori da BikerRoute
              (visibili a ogni utente autenticato) — il pannello admin li linka
              e prima gli admin venivano rimbalzati su /admin. */}
          <Route path="/events/:id/payments" element={<EventPayments />} />
          {/* BRIEFING PRE-EVENTO (28 Ott 2026): pagina gia' pronta (dettagli,
              meteo, partecipanti, percorso, contatti) + endpoint attivo, ma
              senza rotta nessuno poteva aprirla. */}
          <Route path="/events/:id/briefing" element={<RideBriefingPage />} />
          <Route path="/events/:id" element={<EventDetail />} />
          {/* FIX DEALER-NOTIFICHE (Ott 2026): l'inbox notifiche è di TUTTI gli
              utenti autenticati. Era dentro BikerRoute → il dealer che cliccava
              la campanella veniva rimbalzato sulla dashboard e non riusciva a
              leggere le notifiche (l'API è già aperta a ogni ruolo). */}
          <Route path="/notifications" element={<Notifications />} />
          {/* OTT 2026: offerte dei concessionari sul garage del biker. */}
          <Route path="/offerte" element={<DealerOffers />} />
          {/* OTT 2026: inviti alla squadra di un locale (accetta/rifiuta). */}
          <Route path="/inviti-squadra" element={<TeamInvites />} />

          {/* EVENTI (Ott 2026): fuori da BikerRoute e accessibile anche senza
              sessione — Search Console segnalava /events come «Pagina con
              reindirizzamento» perché la rotta era protetta. Qui dentro il
              Layout: chi è loggato vede l'hub con l'app completa, gli anonimi
              la pagina pubblica (Layout rende solo l'Outlet per i path in
              utils/publicRoutes.ts). */}
          <Route path="/events" element={<EventsEntry />} />

          {/* FIX V-02: BikerRoute blocca dealer/admin dall'UI biker */}
          <Route element={<BikerRoute />}>
            <Route path="/dashboard" element={<Dashboard />} />
            {/* TIMBRI EVENTI (28 Ott 2026): pagina già pronta (timbri dei
                motoraduni + statistiche), mancava solo l'accesso. Vive sotto
                /passport/timbri per non collidere con /passport (Ride Passport
                di regioni/passi/circuiti). */}
            <Route path="/passport/timbri" element={<EventPassportPage />} />
            <Route path="/routes/plan" element={<PlanRoute />} />
            <Route
              path="/routes/generate-by-distance"
              element={<GenerateByDistance />}
            />
            <Route path="/routes/record" element={<RecordRoute />} />
            <Route path="/routes/:id/edit" element={<EditRoute />} />
            <Route path="/routes/:id" element={<RouteDetail />} />
            <Route path="/routes" element={<RoutesList />} />
            <Route path="/garage" element={<Garage />} />
            <Route path="/garage/bikes/:id" element={<BikeDetail />} />
            <Route path="/garage/marketplace" element={<Marketplace />} />
            <Route
              path="/garage/marketplace/:bikeId"
              element={<MarketplaceDetail />}
            />
            <Route path="/chapters/new" element={<ChapterCreate />} />
            <Route path="/chapters/:id/stats" element={<ChapterStats />} />
            <Route
              path="/chapters/:id/documents"
              element={<ChapterDocuments />}
            />
            <Route path="/chapters/:id/roles" element={<ChapterRoles />} />
            <Route path="/chapters/:id" element={<ChapterDetail />} />
            <Route path="/chapters" element={<ChaptersList />} />
            <Route path="/my-chapters" element={<UserChapters />} />
            <Route element={<ChapterOwnerRoute />}>
              <Route
                path="/chapters/:id/dashboard"
                element={<ChapterDashboard />}
              />
              <Route path="/chapters/:id/board" element={<ChapterBoard />} />
              <Route
                path="/chapters/:id/challenges"
                element={<ChapterChallenges />}
              />
              <Route path="/chapters/:id/quotas" element={<ChapterQuotas />} />
              <Route
                path="/chapters/:id/membership"
                element={<ChapterMembership />}
              />
              <Route
                path="/chapters/:id/broadcast"
                element={<ChapterBroadcast />}
              />
            </Route>
            <Route path="/events/new" element={<CreateEvent />} />
            <Route path="/events/copilot" element={<EventCopilot />} />
            <Route path="/events/map" element={<EventsMapPage />} />
            <Route path="/events/:id/edit" element={<EditEvent />} />
            <Route path="/events/:id/close" element={<EventClose />} />
            <Route path="/events/:id/budget" element={<EventBudget />} />
            <Route path="/events/biker" element={<EventsList />} />
            <Route path="/events/locali" element={<TappeEvents />} />
            <Route
              path="/rides/new"
              element={<Navigate to="/routes/record" replace />}
            />
            <Route
              path="/rides/:id"
              element={<Navigate to="/routes/record" replace />}
            />
            <Route
              path="/rides"
              element={<Navigate to="/routes/record" replace />}
            />
            <Route path="/nearby" element={<Nearby />} />
            <Route path="/leaderboard" element={<LeaderboardPage />} />
            <Route path="/my-stats" element={<MyStats />} />
            <Route path="/board" element={<Board />} />
            <Route path="/chats" element={<Chats />} />
            <Route path="/radar" element={<Radar />} />
            <Route path="/cerchia" element={<CerchiaPage />} />
            {/* FEAT CONCESSIONARI-VICINI (Ott 2026): dealer di zona con invito
                referral per chi non è ancora su RideConnect. */}
            <Route path="/dealers/nearby" element={<DealersNearby />} />
            {/* FIX DEEPLINK (Ago 2026): redirect URL legacy delle notifiche
                connessione (pre-fix) verso la CerchiaPage con tab corretta */}
            <Route
              path="/connections/pending"
              element={<Navigate to="/cerchia?tab=richieste" replace />}
            />
            <Route
              path="/connections"
              element={<Navigate to="/cerchia" replace />}
            />
            <Route path="/subscribe" element={<Subscribe />} />
            <Route path="/subscribe/checkout" element={<CheckoutPage />} />
            <Route path="/pricing" element={<UnifiedPricing />} />
            <Route path="/referral" element={<ReferralPage />} />
            <Route path="/roadprint" element={<RoadprintPage />} />
            <Route path="/badges" element={<BadgesPage />} />
            <Route path="/chapter-wars" element={<ChapterWarsPage />} />
            <Route path="/feed" element={<SocialFeedPage />} />
            <Route path="/my-rides" element={<MyRidesPage />} />
            <Route path="/passport" element={<RidePassportPage />} />
            <Route path="/wrapped" element={<RideWrappedPage />} />
            <Route path="/calendar" element={<EventCalendar />} />
            <Route path="/appointments" element={<MyAppointments />} />
            <Route path="/garage-access" element={<GarageAccessPage />} />
            <Route path="/challenges" element={<ChallengesPage />} />
            <Route path="/challenges/:id" element={<ChallengeDetailPage />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/settings" element={<UserSettings />} />
            <Route path="/settings/digest" element={<DigestSettings />} />
            <Route
              path="/settings/subscription"
              element={<ManageSubscription />}
            />
          </Route>
          <Route path="/dealers/:id" element={<DealerDetail />} />
          <Route path="/dealers" element={<DealersList />} />

          <Route element={<DealerRoute />}>
            <Route path="/dealer/dashboard" element={<DealerDashboard />} />
            <Route path="/dealer/showcase" element={<DealerShowcase />} />
            <Route path="/dealer/rental" element={<DealerRentalFleet />} />
            <Route path="/dealer/chapters" element={<DealerChapters />} />
            <Route
              path="/dealer/chapters/:id"
              element={<DealerChapterDetail />}
            />
            <Route
              path="/dealer/affiliations"
              element={<DealerAffiliations />}
            />
            <Route path="/dealer/nearby" element={<NearbyDealer />} />
            <Route path="/dealer/analytics" element={<DealerAnalytics />} />
            <Route path="/dealer/events" element={<DealerEvents />} />
            <Route
              path="/dealer/appointments"
              element={<DealerAppointments />}
            />
            <Route path="/dealer/promotions" element={<DealerPromotions />} />
            <Route path="/dealer/leads" element={<DealerLeadsKanban />} />
            <Route path="/dealer/followups" element={<DealerFollowups />} />
            <Route
              path="/dealer/communications"
              element={<DealerCommunications />}
            />
            <Route
              path="/dealer/subscription"
              element={<DealerSubscription />}
            />
            <Route path="/dealer/profile" element={<DealerProfile />} />
          </Route>

          {/* Area Partner / Agente Commerciale */}
          <Route element={<PartnerLayout />}>
            <Route path="/partner/dashboard" element={<PartnerDashboard />} />
            <Route
              path="/partner/referral-codes"
              element={<PartnerReferralCodes />}
            />
            <Route path="/partner/customers" element={<PartnerCustomers />} />
            <Route
              path="/partner/commissions"
              element={<PartnerCommissions />}
            />
            <Route path="/partner/profile" element={<PartnerProfile />} />
          </Route>

          {/* Pannello Agente (FIX AFFILIATE Set 2026) */}
          <Route path="/agent/dashboard" element={<AgentDashboard />} />
        </Route>

        {/* ROUTE ADMIN (FIX DOPPIA-SIDEBAR, Ott 2026): prima erano annidate dentro
            <Layout /> e l'admin mostrava DUE sidebar (quelle dell'app + quella
            admin) rubando ~500px di larghezza al contenuto: le card statistiche
            si schiacciavano e i titoli andavano a capo in verticale. */}
        <Route element={<AdminRoute />}>
          <Route element={<AdminLayout />}>
            <Route path="/admin" element={<AdminDashboardPage />} />
            <Route path="/admin/users" element={<Admin />} />
            <Route path="/admin/users/:id" element={<AdminUserDetail />} />
            <Route path="/admin/dealers" element={<Admin />} />
            <Route path="/admin/tappe" element={<AdminTappePage />} />
            <Route path="/admin/chapters" element={<Admin />} />
            <Route path="/admin/events" element={<AdminEventsPage />} />
            <Route path="/admin/news" element={<AdminNewsPage />} />
            <Route path="/admin/billing" element={<Admin />} />
            <Route
              path="/admin/payments"
              element={<AdminPaymentSettingsPage />}
            />
            <Route
              path="/admin/feature-flags"
              element={<AdminFeatureFlagsPage />}
            />
            <Route path="/admin/moderation" element={<AdminModerationPage />} />
            {/* FIX AB-ADMIN (Ott 2026): il pannello A/B esisteva ma non era
                  raggiungibile da nessuna rotta. */}
            <Route path="/admin/ab-testing" element={<AbTestingAdminPage />} />
            <Route path="/admin/ops" element={<AdminOpsDashboardPage />} />
            <Route path="/admin/audit" element={<AdminOpsDashboardPage />} />
            <Route
              path="/admin/monitoring"
              element={<AdminUserMonitoringPage />}
            />
            <Route path="/admin/settings" element={<AdminSettingsPage />} />
            <Route path="/admin/security" element={<AdminSecurityPage />} />
            <Route path="/admin/tools" element={<AdminToolsPage />} />
            <Route path="/admin/analytics" element={<AdminAnalytics />} />
            <Route path="/admin/rbac" element={<AdminEnterprisePage />} />
            <Route path="/admin/broadcast" element={<BroadcastCenterPage />} />
            <Route
              path="/admin/presentazione"
              element={<AdminOutreachPage />}
            />
            <Route path="/admin/agents" element={<Admin />} />
            <Route
              path="/admin/agents/:id"
              element={<AdminAgentDetailPage />}
            />
            <Route path="/admin/financials" element={<AdminFinancialsPage />} />
          </Route>
        </Route>
      </Route>

      <Route path="/" element={<RootRedirect />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
